// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useEffect, useMemo, useRef } from 'react';

import type { SurveyPart, SurveyType } from '../../../api/types';
import type { CaseActions, CaseData } from '../../../app/bygning/useCaseData';
import { useCanEdit } from '../../../app/CaseRoleContext';
import { useArmedConfirm } from '../../../app/useArmedConfirm';
import { tonnesFigure } from '../../../bygning/measure';
import { isScanPart } from '../../../bygning/provenance';
import type { Selection } from '../../../bygning/selection';
import { roomName } from '../../../kortlaegning/model';
import { resourceIndex } from '../../../kortlaegning/resources';
import { invalidValueMessage } from '../../../kortlaegning/writeOutcome';
import { Button } from '../../Button';
import { IconButton } from '../../IconButton';
import { Pill } from '../../Pill';
import { TextLink } from '../../TextLink';
import { EvidenceButtons } from './EvidenceButtons';
import styles from './Inspector.module.css';
import { PartFields } from './PartFields';
import { ResourceImage } from './ResourceImage';

export interface PartInspectorProps {
  /** The type the part belongs to (where "← {type}" goes back to). */
  type: SurveyType;
  part: SurveyPart;
  data: CaseData;
  sel: Selection;
  setSel: (patch: Partial<Selection>) => void;
  actions: CaseActions;
}

/**
 * One building part's own inspector (Ressourcer's drill-down: `?part=` of the
 * shown type swaps it in for the type's): "← {type}" back, the part's chosen
 * image, its code with Manuel / forældet / ★, the type and room, its tonnes
 * when the server sends them, its own rows (`PartFields`: Mængde, Note, the
 * part-scoped Egenskaber), the evidence buttons, a pointer to the type for
 * everything the parts share, and the footer pinned to the foot of the tile:
 * Markér vigtig (★) and Markér som fejl / Slet del behind an armed confirm.
 * Every write goes through `CaseActions`; a viewer sees no footer
 * (`useCanEdit`) and read-only rows.
 */
export function PartInspector({ type, part, data, sel, setSel, actions }: PartInspectorProps) {
  const home = useRef<HTMLDivElement>(null);
  const canEdit = useCanEdit();
  const { busy } = data;
  const index = useMemo(() => resourceIndex(data.resources), [data.resources]);
  const confirm = useArmedConfirm<string>(busy, part.code);
  const armed = confirm.armed === part.code;
  const scan = isScanPart(part);
  const room = roomName(part);
  const mass = part.mass_t ?? null;

  // The tile keeps its scroll across the swap: a part opened from deep in the type's chips starts at its top.
  useEffect(() => {
    const tile = home.current?.parentElement;
    if (tile) tile.scrollTop = 0;
  }, [part.code]);

  const back = () => setSel({ partCode: null, ev: null });
  const onInvalid = (label: string) => actions.notify(invalidValueMessage(label));

  function remove() {
    const code = part.code;
    void actions.deleteResource(code).then((ok) => {
      if (ok) setSel({ partCode: null, selParts: sel.selParts.filter((c) => c !== code) });
    });
  }

  return (
    <div ref={home} className={styles.root} tabIndex={-1}>
      <div className={styles.back}>
        <TextLink size="sm" onClick={back} aria-label={`Tilbage til typen ${type.name}`}>
          ← {type.name}
        </TextLink>
      </div>
      <ResourceImage
        type={type}
        index={index}
        partCode={part.code}
        canEdit={canEdit}
        onOpen={(code) => setSel({ partCode: code, ev: 'foto' })}
        placement="hero"
      />
      <div className={styles.head}>
        <div className={styles.headMain}>
          <h2 className={styles.title}>{part.code}</h2>
          {!scan && <Pill variant="outline">Manuel</Pill>}
          {part.orphaned && (
            <Pill tone="warn" title="Instansen findes ikke længere — gennemgå eller flyt delen">
              forældet
            </Pill>
          )}
          {part.starred && (
            <span className={styles.star} role="img" aria-label="Vigtig" title="Vigtig">
              ★
            </span>
          )}
        </div>
      </div>
      <div className={styles.meta}>{room ? `${type.name} · ${room}` : type.name}</div>

      {mass !== null && (
        <div className={styles.big}>
          <small className={styles.bigLabel}>Masse</small>
          <b className={styles.bigFigure}>
            {tonnesFigure(mass)}
            <i className={styles.bigUnit}>t</i>
          </b>
        </div>
      )}

      <section className={styles.section} aria-label="Delens felter">
        <PartFields
          type={type}
          part={part}
          resource={index.get(part.code) ?? null}
          catalogue={data.resourceKeys}
          canEdit={canEdit}
          home={home}
          onPatch={(patch) => void actions.patchPart(part.code, patch)}
          onCellCommit={async (keyId, value, label) => {
            await actions.patchResource(part.code, { [keyId]: value }, label);
          }}
          onInvalid={onInvalid}
          layout="page"
        />
      </section>

      <section className={styles.section} aria-label="Evidens">
        <h3 className={styles.sectionHead}>Evidens</h3>
        <EvidenceButtons code={part.code} sel={sel} setSel={setSel} />
      </section>

      <p className={styles.followsType}>
        Øvrige egenskaber (materiale, EAK, behandling …) følger typen {type.name}.{' '}
        <TextLink size="sm" nowrap onClick={back}>
          Åbn typen
        </TextLink>
      </p>

      <div className={styles.spacer} />

      {canEdit && (
        <div className={styles.footer}>
          <div className={styles.actions}>
            <IconButton
              label={part.starred ? 'Fjern vigtig' : 'Markér vigtig'}
              aria-pressed={part.starred}
              className={styles.starToggle}
              disabled={busy}
              onClick={() => void actions.patchPart(part.code, { starred: !part.starred })}
            >
              <span aria-hidden="true">{part.starred ? '★' : '☆'}</span>
            </IconButton>
            <Button
              size="lg"
              tone="danger"
              armed={armed}
              disabled={busy}
              title={scan ? 'Markér delen som fejldetektion' : undefined}
              onBlur={confirm.disarm}
              onClick={(e) => {
                if (!armed) {
                  confirm.arm(part.code, e.currentTarget);
                  return;
                }
                confirm.disarm();
                remove();
              }}
            >
              {armed ? `Bekræft: slet ${part.code}` : scan ? 'Markér som fejl' : 'Slet del'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
